// Whose pick is whose: the order a draft runs in, and the picks that changed
// hands in it (DRAFT_ROOM.md "Trading during the draft").
//
// A PICK IS NAMED BY ITS ROUND AND THE TEAM WHOSE SLOT IT IS, never by its
// overall number alone: a trade made in the lobby, before the order is set,
// still means the pick it said once the order moves. The overall number is
// worked out from the order whenever it is wanted.

import type { DraftState } from './engine';

/** Which team's slot pick `overall` (1-based) is, by the order alone. */
export function teamForPick(state: Pick<DraftState, 'type' | 'order'>, overall: number): string {
  const n = state.order.length;
  const round = Math.ceil(overall / n);
  const idx = (overall - 1) % n;
  return state.order[forward(state.type, round) ? idx : n - 1 - idx]!;
}

/** Whether round `round` runs in the order's own direction. */
function forward(type: DraftState['type'], round: number): boolean {
  return type === 'linear' || (type === 'snake' ? round % 2 === 1 : trrForward(round));
}

/** Third-round reversal: rounds 1 forward, 2 back, 3 back, then alternate. */
function trrForward(round: number): boolean {
  if (round === 1) return true;
  if (round === 2 || round === 3) return false;
  return round % 2 === 0;
}

export function totalPicks(state: Pick<DraftState, 'order' | 'rounds'>): number {
  return state.order.length * state.rounds;
}

/** A pick's key in `DraftState.owners`: its round and the team whose slot it is. */
export function pickKey(round: number, teamId: string): string {
  return `${String(round)}:${teamId}`;
}

/** The overall number of `teamId`'s pick in `round`, or null for a team not in the order. */
export function overallOf(
  state: Pick<DraftState, 'type' | 'order'>,
  round: number,
  teamId: string,
): number | null {
  const n = state.order.length;
  const idx = state.order.indexOf(teamId);
  if (idx < 0) return null;
  return (round - 1) * n + (forward(state.type, round) ? idx : n - 1 - idx) + 1;
}

/** Who makes pick `overall` now: the team whose slot it is, or whoever it was traded to. */
export function ownerOf(
  state: Pick<DraftState, 'type' | 'order' | 'owners'>,
  overall: number,
): string {
  const original = teamForPick(state, overall);
  const round = Math.ceil(overall / state.order.length);
  return state.owners?.[pickKey(round, original)] ?? original;
}

/** Who has a drafted player now: the team that drafted him, or whoever he was traded to. */
export function holderOf(
  state: Pick<DraftState, 'holders'>,
  pick: { teamId: string; playerId: string },
): string {
  return state.holders?.[pick.playerId] ?? pick.teamId;
}
